import { useQuery } from '@tanstack/react-query';
import type { GroupComparisonsFilterParams, GroupComparisonsFilterResult } from '@/features/reports/api/groupComparisons.dto';
import {
  filterParamsToApiParams,
  groupComparisonsApi,
} from '@/features/reports/api/groupComparisons.api';
import { hasAtLeastOneGroupFilter } from '@/features/reports/data/groupComparisonsMock';
import { buildComposedPeerGroupLabel } from '@/features/reports/utils/bandDisplayLabels';

export const groupComparisonsQueryKeys = {
  all: ['group-comparisons'] as const,
  search: (params: GroupComparisonsFilterParams) =>
    [...groupComparisonsQueryKeys.all, 'search', params] as const,
};

const PLACEHOLDER_PARAMS: GroupComparisonsFilterParams = {
  year: '',
  metricFocus: 'cost',
};

/**
 * Loads group comparisons from GET /api/app/group-comparison/group-comparisons.
 */
export function useGroupComparisonsQuery(
  params: GroupComparisonsFilterParams | null,
  enabled: boolean
) {
  const queryEnabled =
    enabled &&
    params != null &&
    Boolean(params.year?.trim()) &&
    hasAtLeastOneGroupFilter(params);

  return useQuery<GroupComparisonsFilterResult>({
    queryKey: groupComparisonsQueryKeys.search(params ?? PLACEHOLDER_PARAMS),
    queryFn: async () => {
      const apiParams = filterParamsToApiParams(params!);
      if (!apiParams) {
        throw new Error('Invalid search parameters.');
      }

      const result = await groupComparisonsApi.getGroupComparisons(apiParams);

      const composedPeerGroupLabel =
        result.composedPeerGroupLabel.trim() ||
        buildComposedPeerGroupLabel({
          governorateName: params!.governorateName,
          schoolBandDisplay: params!.schoolBandDisplay,
          tuitionBandDisplay: params!.tuitionBandDisplay,
          parentBandDisplay: params!.parentBandDisplay,
        });

      return { ...result, composedPeerGroupLabel };
    },
    enabled: queryEnabled,
    staleTime: 0,
  });
}
